import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  useChildMatches,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { UploadCloud } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { useSession } from '~/lib/auth';
import {
  type StudioGenre,
  type StudioPack,
  listFlips,
  listGenres,
  listPacks,
  submitFlip,
  voteFlip,
  voteGenre,
  votePack,
} from '~/lib/studio';
import { buildHead } from '~/seo/site';
import { WaveformPlayer } from '~/ui/WaveformPlayer';
import { Badge } from '~/ui/components/badge';
import { Button } from '~/ui/components/button';
import { ProposeGenreForm } from './genres.propose';
import { UploadPackForm } from './packs.new';

const TABS = ['genres', 'packs', 'flips'] as const;
type Tab = (typeof TABS)[number];
const searchSchema = z.object({ tab: z.enum(TABS).optional() });

export const Route = createFileRoute('/studio')({
  validateSearch: (s) => searchSchema.parse(s),
  head: () =>
    buildHead({
      title: 'Studio',
      description:
        'The Prod Battle Studio: browse and vote on genres, sample packs, and Sample Flip loops, and submit your own for the community and A&R to approve.',
      path: '/studio',
    }),
  component: Studio,
});

function Studio() {
  // /studio/review is a child route; render it through the Outlet instead of
  // the tabbed hub when we're on a nested path.
  const childMatches = useChildMatches();
  const { tab: tabParam } = useSearch({ from: '/studio' });
  const tab: Tab = tabParam ?? 'genres';
  const navigate = useNavigate({ from: '/studio' });
  const { data: session } = useSession();
  const isReviewer = session?.role === 'ar' || session?.role === 'admin';

  if (childMatches.length > 0) return <Outlet />;

  return (
    <section className="mx-auto max-w-3xl px-4 py-10 space-y-6">
      <header className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Studio</h1>
          {isReviewer && (
            <Link
              to="/studio/review"
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:border-primary"
            >
              Review queue
            </Link>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Browse and vote on genres, sample packs, and Sample Flip loops - and submit your own. Top
          community picks get approved by A&R into the shared pool.
        </p>
        <nav className="flex gap-1 border-b border-border pt-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => navigate({ search: { tab: t } })}
              className={`-mb-px border-b-2 px-4 py-2 text-sm capitalize transition-colors ${
                tab === t
                  ? 'border-primary font-medium text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'flips' ? 'Sample Flips' : t}
            </button>
          ))}
        </nav>
      </header>

      {tab === 'genres' && <GenresTab isAuthed={Boolean(session)} />}
      {tab === 'packs' && <PacksTab isAuthed={Boolean(session)} />}
      {tab === 'flips' && <FlipsTab isAuthed={Boolean(session)} />}
    </section>
  );
}

// ─── Genres ──────────────────────────────────────────────────────────────────

function GenresTab({ isAuthed }: { isAuthed: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['studio', 'genres'], queryFn: listGenres });
  const vote = useMutation({
    mutationFn: ({ g }: { g: StudioGenre }) => voteGenre(g.id, g.iVoted),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio', 'genres'] }),
  });
  const items = data ?? [];
  const proposed = items.filter((g) => g.status === 'proposed');
  const active = items.filter((g) => g.status === 'active');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Vote proposed genres into the catalogue.</p>
        {isAuthed && (
          <Button variant="outline" size="sm" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : 'Propose a genre'}
          </Button>
        )}
      </div>
      {adding && isAuthed && (
        <div className="rounded-lg border border-border p-4">
          <ProposeGenreForm
            onDone={() => {
              setAdding(false);
              qc.invalidateQueries({ queryKey: ['studio', 'genres'] });
            }}
          />
        </div>
      )}
      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}

      {proposed.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Up for vote</h2>
          {proposed.map((g) => (
            <div
              key={g.id}
              className="flex items-center justify-between rounded-md border border-border px-3 py-2"
            >
              <span className="font-medium">{g.name}</span>
              <div className="flex items-center gap-3">
                <span className="text-sm tabular-nums text-muted-foreground">{g.voteCount}</span>
                {isAuthed ? (
                  <Button
                    size="sm"
                    variant={g.iVoted ? 'default' : 'outline'}
                    onClick={() => vote.mutate({ g })}
                  >
                    {g.iVoted ? 'Voted' : 'Vote'}
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">Sign in to vote</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">In the catalogue</h2>
        <div className="flex flex-wrap gap-2">
          {active.map((g) => (
            <Badge key={g.id} variant="secondary">
              {g.name}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Packs ───────────────────────────────────────────────────────────────────

function PacksTab({ isAuthed }: { isAuthed: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['studio', 'packs'], queryFn: listPacks });
  const vote = useMutation({
    mutationFn: (id: string) => votePack(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio', 'packs'] }),
  });
  const items = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Pool packs everyone can use, plus your own uploads. Vote up the best.
        </p>
        {isAuthed && (
          <Button variant="outline" size="sm" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : 'Upload a pack'}
          </Button>
        )}
      </div>
      {adding && isAuthed && (
        <div className="rounded-lg border border-border p-4">
          <UploadPackForm
            onDone={() => {
              setAdding(false);
              qc.invalidateQueries({ queryKey: ['studio', 'packs'] });
            }}
          />
        </div>
      )}
      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
      {!isLoading && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No packs yet.
        </div>
      )}
      <ul className="space-y-2">
        {items.map((p: StudioPack) => (
          <li
            key={p.id}
            className="flex items-center justify-between rounded-md border border-border px-3 py-2"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{p.name}</span>
                <Badge variant={p.kind === 'pool' ? 'secondary' : 'outline'}>{p.kind}</Badge>
                {p.isMine && <Badge variant="outline">yours</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">
                {p.genre.name} · {p.stemCount} stems
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm tabular-nums text-muted-foreground">{p.voteCount}</span>
              {isAuthed ? (
                <Button
                  size="sm"
                  variant={p.myVote ? 'default' : 'outline'}
                  disabled={p.myVote}
                  onClick={() => vote.mutate(p.id)}
                >
                  {p.myVote ? 'Voted' : 'Vote'}
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground">Sign in</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Sample Flips ──────────────────────────────────────────────────────────────

function FlipsTab({ isAuthed }: { isAuthed: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'flips'],
    queryFn: () => listFlips(),
  });
  const vote = useMutation({
    mutationFn: (id: string) => voteFlip(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio', 'flips'] }),
  });
  const items = data ?? [];

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        One loop, everyone flips it. Submit your own - the community votes and A&R approves it into
        the pool.
      </p>

      {isAuthed && (
        <FlipSubmitForm
          onSubmitted={() => qc.invalidateQueries({ queryKey: ['studio', 'flips'] })}
        />
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
      {!isLoading && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No flip sources yet. Be the first to submit one.
        </div>
      )}
      <ul className="space-y-3">
        {items.map((f) => (
          <li key={f.id} className="rounded-lg border border-border bg-card p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{f.label}</span>
              <div className="flex items-center gap-3">
                {f.genreSlug && <Badge variant="secondary">{f.genreSlug}</Badge>}
                <span className="text-sm tabular-nums text-muted-foreground">{f.voteCount}</span>
                {isAuthed && (
                  <Button
                    size="sm"
                    variant={f.myVote ? 'default' : 'outline'}
                    disabled={f.myVote}
                    onClick={() => vote.mutate(f.id)}
                  >
                    {f.myVote ? 'Voted' : 'Vote'}
                  </Button>
                )}
              </div>
            </div>
            <WaveformPlayer src={f.url} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function FlipSubmitForm({ onSubmitted }: { onSubmitted: () => void }) {
  const { data: genres } = useQuery({ queryKey: ['studio', 'genres'], queryFn: listGenres });
  const [label, setLabel] = useState('');
  const [genreId, setGenreId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Pick an audio loop.');
      return submitFlip({ file, label, genreId: genreId || undefined });
    },
    onSuccess: () => {
      setLabel('');
      setGenreId('');
      setFile(null);
      setErr(null);
      onSubmitted();
    },
    onError: (e) => setErr((e as Error).message),
  });

  const active = (genres ?? []).filter((g) => g.status === 'active');

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="text-sm font-medium">Submit a flip loop</div>

      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">Name</span>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Soulful vocal chop"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">Genre (optional)</span>
        <select
          value={genreId}
          onChange={(e) => setGenreId(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Any genre</option>
          {active.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </label>

      <div className="text-sm">
        <span className="mb-1 block text-muted-foreground">Audio loop</span>
        <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-border bg-background px-3 py-3 hover:border-primary">
          <UploadCloud className="size-5 shrink-0 text-muted-foreground" />
          {file ? (
            <span className="truncate font-medium text-foreground">{file.name}</span>
          ) : (
            <span>
              Choose an audio file <span className="text-muted-foreground">(WAV, MP3, or OGG)</span>
            </span>
          )}
          <input
            type="file"
            accept="audio/wav,audio/mpeg,audio/mp3,audio/ogg"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="sr-only"
          />
        </label>
      </div>

      {err && <p className="text-xs text-destructive">{err}</p>}
      <Button
        disabled={!file || label.length < 2 || submit.isPending}
        onClick={() => submit.mutate()}
      >
        {submit.isPending ? 'Submitting...' : 'Submit for review'}
      </Button>
    </div>
  );
}
